import {DataTableColumn} from 'mantine-datatable';
import {I18n} from '@aws-amplify/core';
import {Anchor, Text} from '@mantine/core';
import {TableFormattedTimestamp} from '@flexinet/ui-components';

import {BillableUnit} from '../types';
import {BillableUnitStatusBadge} from '../profile/BillableUnitStatusBadge';

// Every column is sortable from its header. Sorting is applied by useTableSort in
// useBillableUnits, over the rows loaded so far.
export const useBillableUnitsTableColumns = (
    {onRecordClick}: { onRecordClick?: (record: BillableUnit) => void },
) => {

    const columns: DataTableColumn<BillableUnit>[] = [
        {
            accessor: 'name',
            title: I18n.get('Billable Unit'),
            sortable: true,
            // A real button when the operator may edit, so it is keyboard-reachable;
            // plain text when they may not (§10: hidden, not disabled).
            render: (record: BillableUnit) => onRecordClick
                ? (
                    <Anchor
                        component='button'
                        type='button'
                        size='sm'
                        aria-label={`${I18n.get('Edit billable unit')} ${record.name}`}
                        data-testid={`billable-unit-edit-${record.id}`}
                        onClick={() => onRecordClick(record)}
                    >
                        {record.name}
                    </Anchor>
                )
                : <Text size='sm'>{record.name}</Text>,
        },
        {
            // FNP-802: the meter is the whole point of the module being visible — it is
            // what connects a priced unit back to what is actually measured. Prefer the
            // server-resolved meterCode; fall back to the raw id if the meter row is gone.
            accessor: 'meter.meterCode',
            title: I18n.get('Meter'),
            sortable: true,
            render: (record: BillableUnit) => (
                <Text size='sm'>{record.meter?.meterCode ?? record.meterId ?? '—'}</Text>
            ),
        },
        {
            // A null factor means 1:1 and is shown as such, but sorts last as a null.
            accessor: 'meteredUnitsPerBillableUnit',
            title: I18n.get('Metered per Unit'),
            sortable: true,
            render: (record: BillableUnit) => {
                const factor = record.meteredUnitsPerBillableUnit ?? 1;
                // Spell out the 1:1 case rather than showing a bare "1" — it reads as a
                // real statement about the unit instead of a magic number.
                return <Text size='sm'>{factor === 1 ? '1 : 1' : `${factor} : 1`}</Text>;
            },
        },
        {
            accessor: 'status',
            title: I18n.get('Status'),
            sortable: true,
            render: (record: BillableUnit) => <BillableUnitStatusBadge status={record.status}/>,
        },
        {
            accessor: 'created',
            title: I18n.get('Created'),
            sortable: true,
            render: (record: BillableUnit) => record.created
                ? <TableFormattedTimestamp record={record} propertyName='created'/>
                : <Text size='sm'>—</Text>,
        },
    ];

    return {
        columns,
    };
};
